/**
 * Per-member usage for the cycle — a port of `bimgo-ai-web`'s
 * `OrgMembersUsageTable`.
 *
 * Four columns: who, requests used, tokens as **used / limit**, and the tokens
 * each has left. Showing consumption against the allowance in the same cell is
 * the point of the table — a bare "234" says nothing an admin can act on, while
 * "234 / 300" says who is about to run out. Requests are a bare count: the
 * monthly request cap was removed (bimgo-ai-api-v3#32), so there is nothing to
 * divide them by. This drops the reference's "Requests Remaining" column and
 * sort, so `bimgo-ai-web` needs the same change.
 *
 * **Which ceiling a row is held to depends on the plan mode.** On a per-seat
 * plan api-v2 exposes one `max_tokens_per_month` and every row divides by the
 * same number. On `org_shared` it withholds that cap — it does not describe a
 * member there — and each row carries its own `monthly_credit_limit` instead, so the
 * arithmetic that used to only look per-user now genuinely is. `memberTokenLimit`
 * resolves it, and the cell, the remaining figure and the sort all read it, so
 * the table can never sort by a ceiling different from the one it prints.
 *
 * The columns follow the data: a denominator and a "remaining" column appear
 * only where a ceiling exists to measure against. A member with no ceiling shows
 * spend with no denominator, which is the honest reading — not a "/ 0" and not
 * the word "Unlimited" against a cap that was merely withheld.
 *
 * The **sort options** follow a slightly different rule from the columns, which
 * is the reference's own asymmetry and is mirrored on purpose; see `sorts`.
 */
import { useMemo, useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import {
  MEMBER_SORTS,
  hasTokenCeiling,
  memberLabel,
  memberTokenLimit,
  remainingFor,
  sortMembers,
  type MemberSort,
  type OrgUsageSummary,
  type OrgUserUsage,
} from "../../lib/usage";

function UsersIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

export function MembersUsageTable({ summary }: { summary: OrgUsageSummary }) {
  const { t } = useLocale();
  const [sort, setSort] = useState<MemberSort>("requests");

  // No request cap: requests are counted, never capped (bimgo-ai-api-v3#32).
  const limits = {
    tokens: summary.plan?.max_tokens_per_month ?? null,
  };

  // Some members may be capped and others not, so the *column* cannot key off
  // the plan alone — a plan-level test would hide a column half the table can
  // fill.
  const showTokenRemaining = hasTokenCeiling(summary.users, limits.tokens);

  /**
   * Only the sorts the table offers. A "most remaining" order over rows that
   * have no ceiling sorts every one of them as null — a control that appears to
   * do nothing, which reads as a broken table rather than an absent ceiling.
   *
   * **The token sort is gated on the plan cap, not on the column's own test**,
   * and the mismatch is deliberate: it is what `bimgo-ai-web` does
   * (`OrgMembersUsageTable`, the `tokenLimit != null` guard on the select item
   * beside a `hasTokenLimit` guard on the header). The consequence on an
   * `org_shared` plan is a Tokens-Remaining column that cannot be sorted by,
   * since the plan cap there is always withheld. Mirrored rather than corrected
   * on the owner's instruction (2026-09-03) — the two clients showing the same
   * organisation the same controls is worth more than tidying one of them, and
   * a lone fix here would read as a bug against the web client. Change both, or
   * neither.
   */
  const sorts = MEMBER_SORTS.filter(
    (key) => key !== "remainingTokens" || limits.tokens !== null,
  );
  // A sort the table stops offering must not stay in force — on org_shared
  // `remainingTokens` is never offered, so a stored choice of it would order the
  // rows by a key with no control to explain it.
  const activeSort = sorts.includes(sort) ? sort : "requests";

  const rows = useMemo(
    () => sortMembers(summary.users, activeSort, limits),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [summary.users, activeSort, limits.tokens],
  );

  /** "234 / 300", or just "234" when there is no ceiling to divide by. */
  const against = (used: number, limit: number | null) => (
    <>
      {used.toLocaleString()}
      {limit !== null ? <span className="num-limit"> / {limit.toLocaleString()}</span> : null}
    </>
  );

  const left = (used: number, limit: number | null) => {
    const value = remainingFor(used, limit);
    // No ceiling means nothing to be short of — a dash, not a zero.
    return value === null ? "—" : value.toLocaleString();
  };

  /** The credit ceiling this row is held to — plan cap, or its own. */
  const tokenLimitOf = (u: OrgUserUsage) => memberTokenLimit(u, limits.tokens);

  return (
    <div className="settings-card">
      <div className="card-head-row">
        <h3 className="card-title">
          <span className="card-title-icon">
            <UsersIcon />
          </span>
          {t("dashboard.admin.members.title")}
        </h3>
        <select
          className="settings-input select-sm"
          value={activeSort}
          aria-label={t("dashboard.admin.members.sortLabel")}
          onChange={(e) => setSort(e.target.value as MemberSort)}
        >
          {sorts.map((key) => (
            <option key={key} value={key}>
              {t(`dashboard.admin.members.sort.${key}`)}
            </option>
          ))}
        </select>
      </div>

      {rows.length === 0 ? (
        <p className="settings-note is-centered">{t("dashboard.admin.members.empty")}</p>
      ) : (
        <div className="members-scroll">
          <table className="members-table">
            <thead>
              <tr>
                <th>{t("dashboard.admin.members.colEmail")}</th>
                <th>{t("dashboard.admin.members.colRequests")}</th>
                <th>{t("dashboard.admin.members.colTokens")}</th>
                {showTokenRemaining ? (
                  <th>{t("dashboard.admin.members.colRemainingTokens")}</th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.user_id}>
                  {/* Nullable by design — the row still carries real usage, so
                      it degrades to the id rather than being dropped. */}
                  <td className="member-email">{memberLabel(u)}</td>
                  <td className="num">{u.requests_this_month.toLocaleString()}</td>
                  <td className="num">{against(u.tokens_this_month, tokenLimitOf(u))}</td>
                  {showTokenRemaining ? (
                    <td className="num">{left(u.tokens_this_month, tokenLimitOf(u))}</td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
